import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { parseDateInput } from './utils/dates';
import RmaAnalyticsCard from './RmaAnalyticsCard';

import NciAnalyticsCard from './NciAnalyticsCard';
import DeviceAnalyticsCard from './DeviceAnalyticsCard';
import BuildOrderAnalyticsCard from './BuildOrderAnalyticsCard';
import MtbfAnalyticsCard from './MtbfAnalyticsCard';
import MtbfEachDeviceCard from './MtbfEachDeviceCard';
import MtbfCustomerPnEluCard from './MtbfCustomerPnEluCard';
import MtbfProductSeriesCard from './MtbfProductSeriesCard';
import MtbfLifecycleGraphCard from './MtbfLifecycleGraphCard';
import { useUser } from '@clerk/clerk-react';
import LoadingSpinner from './components/LoadingSpinner';
import DataQualityAnalyticsCard from './DataQualityAnalyticsCard';
import SwitchViewGrid from './SwitchViewGrid';
import ProductionData from './analytics/production/ProductionData';
import { computeDataQualityScore, computeAverageScore } from './dataQualityUtils';
import { getWarrantyStatus } from './warrantyUtils';
import { getProductPdf } from './productFiles';
import NoAccessNotice from './components/NoAccessNotice';

const API_BASE = process.env.REACT_APP_API_URL;

export default function AnalyticsPage() {
  const [rmaStats, setRmaStats] = useState(null);
  const [warrantyStats, setWarrantyStats] = useState(null);
  const [prodStats, setProdStats] = useState(null);
  const [prodYears, setProdYears] = useState([]);
  const [nciStats, setNciStats] = useState(null);
  const [deviceStats, setDeviceStats] = useState(null);
  const [buildStats, setBuildStats] = useState(null);
  const [mtbfData, setMtbfData] = useState(null);
  const [mtbfLifecycle, setMtbfLifecycle] = useState(null);
  const [activeTab, setActiveTab] = useState('rma');
  const [showSwitchView, setShowSwitchView] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialProdYear = searchParams.get('prodYear') || 'total';
  const [prodYear, setProdYear] = useState(initialProdYear);
  const [prodExplain, setProdExplain] = useState(false);
  const [year, setYear] = useState('current');
  const [dataQualityAvg, setDataQualityAvg] = useState(null);
  const [dqLoading, setDqLoading] = useState(true);
  const { user, isSignedIn } = useUser();
  const role = user?.publicMetadata?.role || 'guest';
  const isAdmin = role === 'admin';
  const normalizedRole = String(role).toLowerCase().trim();
  const isService = normalizedRole.startsWith('service');

  useEffect(() => {
    if (!isAdmin && !isService) return;
    fetch(`${API_BASE}/analytics/rma?year=${year}`)
      .then(res => res.json())
      .then(setRmaStats)
      .catch(err => console.error('Error fetching RMA analytics:', err));

    fetch(`${API_BASE}/analytics/warranty-ratio?year=${year}`)
      .then(res => res.json())
      .then(setWarrantyStats)
      .catch(() => {});

    fetch(`${API_BASE}/analytics/nci`)
      .then(res => res.json())
      .then(setNciStats)
      .catch(err => console.error('Error fetching NCI analytics:', err));

    fetch(`${API_BASE}/analytics/device`)
      .then(res => res.json())
      .then(setDeviceStats)
      .catch(err => console.error('Error fetching device analytics:', err));

    fetch(`${API_BASE}/analytics/build-orders`)
      .then(res => res.json())
      .then(setBuildStats)
      .catch(err => console.error('Error fetching build order analytics:', err));

    fetch(`${API_BASE}/analytics/mtbf`)
      .then(res => res.json())
      .then(setMtbfData)
      .catch(err => {
        console.error('Error fetching MTBF:', err);
        setMtbfData(null);
      });

    fetch(`${API_BASE}/analytics/mtbf-lifecycle`)
      .then(res => res.json())
      .then(setMtbfLifecycle)
      .catch(err => {
        console.error('Error fetching MTBF lifecycle:', err);
        setMtbfLifecycle(null);
      });

    (async () => {
      try {
        setDqLoading(true);
        const listRes = await fetch(`${API_BASE}/device/devices?limit=1000`);
        const devices = await listRes.json();
        const scores = await Promise.all(
          devices.map(async d => {
            try {
              const detailRes = await fetch(`${API_BASE}/device/${d._id}`);
              const detail = await detailRes.json();
              const warrantyStatus = getWarrantyStatus(detail.scandate);
              const manualAvailable = Boolean(getProductPdf(detail.productNumber));
              let operatingHours = detail.operatingHours;
              if (operatingHours == null && detail.scandate) {
                const sd = parseDateInput(detail.scandate);
                if (sd) {
                  operatingHours = Math.floor((Date.now() - sd.getTime()) / 3600000);
                }
              }
              return computeDataQualityScore({
                ...detail,
                manualAvailable,
                warrantyStatus,
                operatingHours,
              });
            } catch (e) {
              return null;
            }
          })
        );
        const valid = scores.filter(s => typeof s === 'number');
        setDataQualityAvg(computeAverageScore(valid));
      } catch (err) {
        console.error('Error computing data quality average:', err);
        setDataQualityAvg(null);
      } finally {
        setDqLoading(false);
      }
    })();
  }, [isAdmin, isService, year]);

  useEffect(() => {
    if (!isAdmin && !isService) return;
    const url = `${API_BASE}/analytics/production?year=${prodYear}${prodExplain ? '&explain=true' : ''}`;
    fetch(url)
      .then(res => res.json())
      .then(setProdStats)
      .catch(err => console.error('Error fetching production analytics:', err));
  }, [isAdmin, isService, prodYear, prodExplain]);

  useEffect(() => {
    if (!isAdmin && !isService) return;
    fetch(`${API_BASE}/analytics/production/years`)
      .then(res => res.json())
      .then(setProdYears)
      .catch(err => console.error('Error fetching production years:', err));
  }, [isAdmin, isService]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (params.get('prodYear') !== prodYear) {
      params.set('prodYear', prodYear);
      setSearchParams(params, { replace: true });
    }
  }, [prodYear, searchParams, setSearchParams]);

  if (!isSignedIn || (!isAdmin && !isService)) {
    return (
      <div className="ni-page">
        <div className="ni-shell ni-shell--narrow">
          <NoAccessNotice pageName="Analytics" />
        </div>
      </div>
    );
  }

  if (
    !rmaStats ||
    !warrantyStats ||
    !prodStats ||
    !nciStats ||
    !deviceStats ||
    !buildStats ||
    !mtbfData ||
    !mtbfLifecycle
  ) {
    return (
      <div className="ni-page">
        <div className="ni-shell ni-shell--narrow">
          <div className="ni-panel" style={loadingStyle}>
            <LoadingSpinner size={64} />
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="ni-page">
      <div className="ni-shell">
        <header className="ni-header">
          <div className="ni-brand">
            <Link to="/" title="Back to main page" aria-label="Back to main page" style={{ display: 'inline-flex', lineHeight: 0 }}>
              <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            </Link>
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">Analytics</span>
              <span className="ni-brand-subtitle">Operational and warranty performance</span>
            </div>
          </div>
          <button
            onClick={() => setShowSwitchView(v => !v)}
            className="ni-button ni-button--muted"
            style={switchButtonStyle}
          >
            {showSwitchView ? 'Back to curated view' : 'Open explorer'}
          </button>
        </header>

        {showSwitchView ? (
          <div className="ni-panel">
            <SwitchViewGrid isAdmin={isAdmin} userId={user?.id} />
          </div>
        ) : (
          <>
            <div className="ni-panel ni-panel--dense">
              <div className="ni-tabs">
                {[
                  ['rma', 'RMA Analytics'],
                  ['production', 'Production Data'],
                  ['build', 'Production KPIs'],
                  ['nci', 'NCI Analytics'],
                  ['device', 'Device Analytics'],
                  ['dataQuality', 'Data Quality'],
                  ['mtbf', 'MTBF'],
                  ['mtbfEach', 'MTBF Each Device'],
                  ['mtbfCustomer', 'MTBF Customer PN ELU'],
                  ['mtbfSeries', 'MTBF Product Series'],
                  ['mtbfGraph', 'MTBF Graph'],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`ni-tab${activeTab === key ? ' ni-tab--active' : ''}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'rma' && (
              <RmaAnalyticsCard
                stats={rmaStats}
                warranty={warrantyStats}
                year={year}
                setYear={setYear}
              />
            )}
            {activeTab === 'production' && (
              <ProductionData
                stats={prodStats}
                year={prodYear}
                setYear={setProdYear}
                years={prodYears}
                explain={prodExplain}
                setExplain={setProdExplain}
              />
            )}
            {activeTab === 'build' && <BuildOrderAnalyticsCard stats={buildStats} />}
            {activeTab === 'nci' && <NciAnalyticsCard stats={nciStats} />}
            {activeTab === 'device' && <DeviceAnalyticsCard stats={deviceStats} />}
            {activeTab === 'dataQuality' && (
              <DataQualityAnalyticsCard
                average={dataQualityAvg}
                loading={dqLoading}
              />
            )}
            {activeTab === 'mtbf' && (
              <MtbfAnalyticsCard
                data={mtbfData}
                failureCount={warrantyStats.allTimeAcceptedNin}
              />
            )}
            {activeTab === 'mtbfEach' && (
              <MtbfEachDeviceCard products={mtbfData.byProduct} />
            )}
            {activeTab === 'mtbfCustomer' && (
              <MtbfCustomerPnEluCard groups={mtbfData.byCustomerPN} />
            )}
            {activeTab === 'mtbfSeries' && (
              <MtbfProductSeriesCard groups={mtbfData.byProductSeries} />
            )}
            {activeTab === 'mtbfGraph' && (
              <MtbfLifecycleGraphCard
                points={mtbfLifecycle.points || mtbfLifecycle}
                failureCount={warrantyStats.allTimeAcceptedNin}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

const loadingStyle = {
  minHeight: '50vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};

const switchButtonStyle = {
  minHeight: '44px'
};





